package utils

import kotlin.js.Promise

// Common external declarations for browser APIs
// This file contains shared browser API declarations used across the project

// Console API
external val console: Console
external interface Console {
    fun log(vararg args: Any?)
    fun warn(vararg args: Any?)
    fun error(vararg args: Any?)
}

// Event APIs
external interface Event {
    val type: String
    val target: EventTarget?
}

external interface MessageEvent : Event {
    val data: dynamic
    val origin: String?
    val lastEventId: String?
}

external interface EventTarget {
    fun addEventListener(type: String, listener: ((Event) -> Unit)?)
    fun removeEventListener(type: String, listener: ((Event) -> Unit)?)
}

// EventSource API for Server-Sent Events
external interface EventSource {
    val url: String
    val readyState: Short
    var onopen: ((Event) -> Unit)?
    var onmessage: ((MessageEvent) -> Unit)?
    var onerror: ((Event) -> Unit)?
    fun addEventListener(type: String, listener: ((Event) -> Unit)?)
    fun removeEventListener(type: String, listener: ((Event) -> Unit)?)
    fun close()
}

external fun EventSource(url: String): EventSource

// EventSource constants
object EventSourceConstants {
    const val CONNECTING: Short = 0
    const val OPEN: Short = 1
    const val CLOSED: Short = 2
}

// Navigator and Service Worker APIs
external val navigator: Navigator
external interface Navigator {
    val serviceWorker: ServiceWorkerContainer?
}

external interface ServiceWorkerContainer {
    val ready: Promise<ServiceWorkerRegistration>?
    fun register(scriptUrl: String): Promise<ServiceWorkerRegistration>
}

external interface ServiceWorkerRegistration {
    val pushManager: PushManager
    fun showNotification(title: String, options: NotificationOptions = definedExternally)
}

external interface PushManager {
    fun subscribe(options: PushSubscriptionOptions): Promise<PushSubscription>
    fun getSubscription(): Promise<PushSubscription?>
}

external interface PushSubscriptionOptions {
    val userVisibleOnly: Boolean
    val applicationServerKey: ByteArray?
}

external interface PushSubscription {
    val endpoint: String
    val keys: PushSubscriptionKeys?
    fun toJSON(): PushSubscriptionJSON
    fun unsubscribe(): Promise<Boolean>
}

external interface PushSubscriptionKeys {
    val p256dh: String?
    val auth: String?
}

external interface PushSubscriptionJSON {
    val endpoint: String
    val keys: PushSubscriptionJSONKeys?
}

external interface PushSubscriptionJSONKeys {
    val p256dh: String?
    val auth: String?
}

// Notification API
external interface NotificationObject {
    fun requestPermission(): Promise<String>
}

external val Notification: NotificationObject

external enum class NotificationPermission {
    Granted,
    Denied,
    Default
}

external interface NotificationOptions {
    val body: String?
        get() = definedExternally
    val icon: String?
        get() = definedExternally
    val badge: String?
        get() = definedExternally
    val data: dynamic?
        get() = definedExternally
    val tag: String?
        get() = definedExternally
    val requireInteraction: Boolean?
        get() = definedExternally
}

// Window and Fetch APIs
external val window: Window
external interface Window {
    fun fetch(url: String, init: RequestInit = definedExternally): Promise<Response>
}

external interface RequestInit {
    var method: String
    var headers: dynamic
    var body: String?
}

external interface Response {
    val ok: Boolean
    val status: Int
    fun json(): Promise<dynamic>
}

// Helper function to check MessageEvent type
fun isMessageEvent(event: Event): Boolean {
    return event.asDynamic().data != null
}

// Helper function to cast Event to MessageEvent
fun Event.asMessageEvent(): MessageEvent? {
    return if (isMessageEvent(this)) {
        this.asDynamic()
    } else {
        null
    }
}

